<template>
  <div id="license-dashboard" class="h-full">
    <detail-content-layout label="ダッシュボード" :hide-on-button-click="true">
      <div class="card">
        <!-- ヘッダ領域 -->
        <div class="card-header-title">保有中の資格・免許一覧</div>

        <!-- ag-grid テーブル -->
        <ag-grid-vue
          ref="agGridTableLicense"
          :gridOptions="gridOptions"
          class="ag-theme-material w-full ag-grid-table"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :rowData="getHavingLicenseForDashboard"
          domLayout="autoHeight"
          :animateRows="true"
          :floatingFilter="false"
          :pagination="false"
          :paginationPageSize="null"
          :suppressPaginationPanel="true"
          :suppressDragLeaveHidesColumns="true"
          :suppressRowClickSelection="true"
          :suppressCellFocus="true"
          :suppressNoRowsOverlay="false"
          @grid-ready="onGridReady"
          @rowClicked="onRowClicked"
        />
      </div>
      <div class="card">
        <!-- ヘッダ領域 -->
        <div class="card-header-title">新規追加 / 更新</div>

        <!-- 資格一覧 -->
        <div class="license-categories-accordion">
          <div
            v-for="license in licenseCategoriesWithOnClick"
            :key="license.title"
          >
            <div class="item-wrapper">
              <accordion-data
                :title="license.title"
                :items="license.accordionData"
              />
            </div>
          </div>
        </div>
      </div>
    </detail-content-layout>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import { ColorMixin } from "@/mixins";
import CellRendererLicenseStatus from "./cell-renderer/CellRendererLicenseStatus.vue";
import DetailContentLayout from "../../components/DetailContentLayout.vue";
import AccordionData from "../../components/AccordionData.vue";
import { mapGetters } from "vuex";

export default {
  name: "LicenseDashboard",
  components: {
    AgGridVue,
    CellRendererLicenseStatus,
    DetailContentLayout,
    AccordionData,
  },
  mixins: [ColorMixin],
  computed: {
    ...mapGetters("crewSettingsLicense", [
      "getLicenseDefinitionForDashboard",
      "getHavingLicenseForDashboard",
      "getLicenseDefinition",
      "getHavingLicense",
    ]),

    /**
     * AccordionData用にonClickを追加した資格定義リスト
     */
    licenseCategoriesWithOnClick() {
      return this.getLicenseDefinitionForDashboard.map(category => {
        const accordionData = category.accordionData.map(item => {
          // 子要素がある場合（サブカテゴリがある場合）
          if (item.children && item.children.length > 0) {
            return {
              ...item,
              children: item.children.map(child => ({
                ...child,
                onClick: () =>
                  this.handleLicenseClick(
                    child.category,
                    child.subCategory,
                    child.id
                  ),
              })),
            };
          }
          // 子要素がない場合（サブカテゴリがない場合）
          return {
            ...item,
            onClick: () =>
              this.handleLicenseClick(item.category, item.subCategory, item.id),
          };
        });

        return {
          ...category,
          accordionData,
        };
      });
    },
  },
  data() {
    return {
      gridApi: null,
      gridOptions: {
        localeText: {
          noRowsToShow:
            '<div id="no-rows-license-list" class="aisea-text-16-400-black">表示する資格・免許情報がありません</div>',
        },
        context: {
          componentParent: this,
        },
      },
      defaultColDef: {
        sortable: false,
        resizable: true,
        suppressMenu: true,
      },
      columnDefs: [],
      components: {
        CellRendererLicenseStatus,
      },
      isReadyPagination: false,
    };
  },

  mounted() {
    this.initializeColumnDefs();
    this.$store.dispatch("crewSettingsLicense/fetchAllLicenseData", {
      enterpriseCrewId: this.$route.params.id,
    });
  },
  methods: {
    initializeColumnDefs() {
      this.columnDefs = [
        {
          headerName: "資格・免許区分",
          field: "category",
          minWidth: 165,
          flex: 1,
        },
        {
          headerName: "資格・免許名",
          field: "name",
          minWidth: 200,
          flex: 1,
        },
        {
          headerName: "有効期限",
          field: "expiration",
          minWidth: 100,
          maxWidth: 100,
        },
        {
          headerName: "次回更新期限",
          field: "nextUpdateDate",
          minWidth: 160,
          maxWidth: 160,
        },
        {
          headerName: "ステータス",
          field: "status",
          minWidth: 160,
          maxWidth: 160,
          cellRenderer: "CellRendererLicenseStatus",
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%",
          },
        },
      ];
    },

    onGridReady(params) {
      this.gridApi = params.api;
    },

    /**
     * 行クリック時の処理
     * @param {Object} params - ag-gridのrowClickedイベントパラメータ
     */
    onRowClicked(params) {
      const rowData = params.data;
      if (!rowData) {
        return;
      }

      // カテゴリコードを取得
      const category = rowData.categoryCode;
      if (category) {
        this.$router.push({
          name: "enterprise-management-crew-settings-license",
          params: {
            id: this.$route.params.id,
          },
          query: {
            category: category,
          },
        });
      }
    },

    /**
     * 日付をフォーマット（YYYY/MM/DD形式）
     * @param {Date} date - 日付オブジェクト
     * @returns {String} フォーマットされた日付文字列
     */
    formatDate(date) {
      if (!date || !(date instanceof Date)) {
        return "";
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}/${month}/${day}`;
    },

    /**
     * ステータスチップがクリックされた時の処理
     * @param {Object} data - 行データ
     */
    onStatusClick(data) {
      console.log("Status clicked:", data);

      // TODO: ステータスクリック時に画面遷移
      this.$vs.notify({
        title: "ステータスクリック",
        text: `${data.name}のステータス: ${data.status}`,
        color: "primary",
        iconPack: "feather",
        icon: "icon-info",
      });
    },

    /**
     * 資格項目クリック時の処理
     * @param {String} categoryType - 大区分カテゴリ
     * @param {String} subCategory - 小区分（オプション）
     * @param {String} licenseType - 資格タイプ
     */
    handleLicenseClick(categoryType, subCategory, licenseType) {
      // 保有資格新規追加ページへ遷移
      const query = {
        category: categoryType,
        license: licenseType,
      };
      if (subCategory) {
        query.subCategory = subCategory;
      }
      this.$router.push({
        name: "enterprise-management-crew-settings-license-add",
        params: {
          id: this.$route.params.id,
        },
        query,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";
@import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";

.ag-grid-table {
  height: 100%;
  border: 1px solid $aisea-mouse-gray;
  border-radius: 6px !important;
  overflow: hidden !important;

  font-family: $aisea-font-family;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  .ag-header-cell-text {
    font-family: $aisea-font-family;
    font-size: 12px;
    font-weight: 600;
  }
}

.card {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0px 24px 16px 24px;
  border-radius: 6px;
  box-shadow: 2px 2px 4px 4px #00000024;
}

.card-header-title {
  min-height: 50px;
  display: flex;
  align-items: center;
  padding: 18px 0px;
  font-family: Hiragino Sans;
  font-weight: 600;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0%;
  vertical-align: middle;
}

.license-categories-accordion {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;

  // アイテムラッパー
  .item-wrapper {
    min-width: 320px;
  }
}
</style>
